<template>
	<section v-if="!isUserSubscribedToNewsletter && !selectedBranchLabelFresh" class="container">
		<div class="bg-gradient-to-r from-yellow-600 to-orange-700 rounded-3xl p-6 md:p-10 text-body-primary flex flex-col lg:flex-row max-lg:gap-4">
			<div class="flex-1 flex items-start lg:items-center gap-4 w-full">
				<div class="flex-none max-lg:order-2 size-12 lg:size-24 flex items-center justify-center">
					<NuxtIcon
						v-if="isSent"
						name="custom/mail"
						class="text-icon-inverse text-[4.625rem]"
						filled
					/>
					<NuxtImg
						v-else-if="blok.image?.filename"
						provider="storyblok"
						width="64"
						height="64"
						:modifiers="storyblokFocusModifiers(blok.image.focus)"
						:src="blok.image.filename"
						:alt="blok.image.alt || ''"
						class="max-lg:w-8"
					/>
					<NuxtIcon
						v-else
						name="custom/percentage"
						class="text-icon-inverse text-[4rem]"
						filled
					/>
				</div>
				<div class="flex-1 justify-between items-center max-lg:space-y-4">
					<template v-if="userIsLogged">
						<h2 v-if="blok.title" class="text-[1.25rem] md:text-2xl font-bold">
							<template v-if="blok.preferenceId === specialOfferPreferenceId">
								Nastavit odběr akční nabídky
							</template>
							<template v-else-if="blok.preferenceId === restaurantOfferPreferenceId">
								Nastavit odběr nabídky z restaurace
								<template v-if="houseNumber">
									- {{ selectedHypermarketInstance?.content.name.split(/[-‒–—]/).join(' ') }}
								</template>
								<template v-else>
									- dle vašeho výběru
								</template>
							</template>
							<template v-else>
								Nastavit odběr
							</template>
						</h2>
					</template>
					<template v-else>
						<template v-if="isSent">
							<h2 class="text-[1.25rem] md:text-2xl font-bold">
								Právě jsme vám poslali e-mail k ověření
							</h2>
							<div class="typo-body-m [&_a]:!text-body-primary">
								Nevíte si rady? Zavolejte na naši bezplatnou linku <a :href="`tel:${tel.replace(/ /g,'')}`" class="underline font-bold" @click="trackLinkClickEvent">{{ tel }}</a>.
							</div>
						</template>
						<template v-else-if="registeredUser">
							<h2 class="text-[1.25rem] md:text-2xl font-bold">
								Vypadá to, že tento e-mail už je součástí našeho věrnostního programu.
							</h2>
							<div class="typo-body-m [&_a]:!text-body-primary">
								Své preference můžete kdykoliv upravit ve svém účtu.
							</div>
						</template>
						<template v-else>
							<h2 v-if="blok.title" class="text-[1.25rem] md:text-2xl font-bold">
								{{ blok.title }}
							</h2>

							<RichText v-if="blok.comment && isDesktop" class="typo-body-m [&_a]:!text-body-primary [&_p]:!mb-1.5 [&_p]:last:!mb-0" :data="blok.comment" />
						</template>
					</template>
				</div>
			</div>
			<div v-if="!isSent && !userIsLogged && !registeredUser" class="flex-1 items-center lg:flex lg:flex-col">
				<div v-if="isLoading" class="py-6 flex items-center justify-center w-full">
					<NuxtIcon
						name="custom/loader"
						class="text-icon-inverse p-2 text-3xl block [&_svg]:animate-spin [&_svg]:mx-auto"
						filled
					/>
				</div>
				<template v-else>
					<FormKit
						#default="{state: {valid}}"
						v-model="formModel"
						type="form"
						form-class="flex flex-col md:flex-row w-full relative gap-2 items-end lg:ml-auto max-lg:mb-4"
						:actions="false"
						:classes="{
							form: 'max-md:mx-auto',
						}"
						@submit="submitHandler"
					>
						<FormKit
							type="text"
							label="E-mail:"
							name="email"
							placeholder="Zadejte váš e-mail"
							validation="emailCustomRuleOptional"
							:validation-messages="{
								emailCustomRuleOptional: 'Vaše e-mailovová adresa musí být ve tvaru `email@domena.cz`.',
							}"
							:classes="{
								label: 'mb-2',
								input: 'py-3',
								outer: 'grow max-md:w-full md:relative',
								messages: 'bg-red-100 px-4 pb-3 [&_li]:last:!mb-0 rounded-b-2xl !-mt-1.5 pt-4 md:absolute md:top-full md:left-0 md:right-0 md:z-[5]',
								message: '!text-sm',
								wrapper: 'md:relative md:z-10',
							}"
						/>
						<FormKit
							type="submit"
							:classes="{
								input: 'btn btn-secondary btn-lg text-body-primary !shadow-sm max-md:w-full max-md:justify-center',
								outer: 'max-md:w-full',
							}"
							:disabled="!valid || isLoading || isEmailEmpty"
							aria-label="Odebírat"
						>
							Odebírat
							<NuxtIcon name="custom/arrow-right" filled class="text-lg" />
						</FormKit>
					</FormKit>
					<div v-if="errorMessage" class="w-full bg-red-100 p-4 rounded-2xl text-red-1000 mt-2">
						{{ errorMessage }}
					</div>
				</template>

				<RichText v-if="blok.comment && !isDesktop" class="typo-body-m [&_a]:!text-body-primary max-lg:mt-4 [&_p]:!mb-1.5 [&_p]:last:!mb-0" :data="blok.comment" />
			</div>

			<div v-if="userIsLogged || registeredUser" class="items-center lg:flex">
				<CrmRedirectButton
					v-if="houseNumber"
					class="btn btn-secondary btn-lg shadow-sm max-lg:w-full justify-center"
					:user-state="UserState.UPDATE_PROFILE_PAGE_PREFERENCE_ALERTS"
				>
					<template v-if="userIsLogged">
						Odebírat
					</template>
					<template v-else-if="registeredUser">
						Přihlásit se
					</template>
					<NuxtIcon name="custom/arrow-right" filled class="text-lg" />
				</CrmRedirectButton>
				<button v-else class="btn btn-secondary btn-lg shadow-sm max-lg:w-full justify-center" @click="showHypermarketSelectionBanner = true">
					Odebírat
					<NuxtIcon name="custom/arrow-right" filled class="text-lg" />
				</button>
			</div>
		</div>
		<PartialsHypermarketSelectionPopup
			v-model:visible="showHypermarketSelectionBanner"
		/>
	</section>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useCustomerProfileStore} from '~/stores/customerProfile';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {GsoaContactsService, type Gsoa_CustomerProfile_PreferencyModel as GsoaCustomerProfilePreferency} from '~/openapi';
	import type {NewsletterSignupBannerStoryblok} from '@/storyblok/types';
	import {tel} from '~/resources';
	import {trackLinkClickEvent} from '~/utils/tracking';
	import {UserState} from '~/stores/crmStateStore';

	interface Props {
		blok: NewsletterSignupBannerStoryblok
	}

	const props = defineProps<Props>();
	const formModel = ref({email: ''});

	const {redirectToCrm} = useCrmRedirect();
	const customerProfileStore = useCustomerProfileStore();
	const {userIsLogged, profile} = storeToRefs(customerProfileStore);

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const selectedBranchLabelFresh = computed(() => selectedHypermarketInstance.value?.branchType === 'fresh');

	const houseNumber = computed(() => selectedHypermarketInstance.value?.content.gsoaId);

	// Issue #3144
	const specialOfferPreferenceId = '1010';
	const restaurantOfferPreferenceId = '1011';

	// https://gitlab.com/apploud/globus-mujglobusweb/-/issues/3144#note_3092962968
	const isUserSubscribedToNewsletter = computed(() => {
		const preferenceId = Number(props.blok.preferenceId);

		if (props.blok.preferenceId === specialOfferPreferenceId) {
			// Akční nabídka (1010): skrýt banner globálně, pokud má uživatel tuto preferenci kdekoliv
			return !!profile.value?.preferencies?.find(
				(preference: GsoaCustomerProfilePreferency) => preference.preferencyID === preferenceId,
			);
		}

		if (props.blok.preferenceId === restaurantOfferPreferenceId) {
			// Restaurace (1011): skrýt banner pouze pokud uživatel odebírá pro aktuálně vybraný HM
			return !!profile.value?.preferencies?.find(
				(preference: GsoaCustomerProfilePreferency) =>
					preference.preferencyID === preferenceId
					&& preference.houseNumber === Number(houseNumber.value),
			);
		}

		// Výchozí chování: kontrola preferencyID i houseNumber
		return !!profile.value?.preferencies?.find(
			(preference: GsoaCustomerProfilePreferency) =>
				preference.houseNumber === Number(houseNumber.value)
				&& preference.preferencyID === preferenceId,
		);
	});

	const showHypermarketSelectionBanner = ref(false);

	const breakpoints = useTailwindBreakpoints();
	const isDesktop = breakpoints.greaterOrEqual('lg');

	const isSent = ref(false);
	const isLoading = ref(false);
	const errorMessage = ref('');
	const registeredUser = ref(false);

	const isEmailEmpty = computed(() => !formModel.value.email || formModel.value.email.trim() === '');

	const {executeRecaptcha} = useGoogleRecaptcha();

	watch(showHypermarketSelectionBanner, (newValue) => {
		if (newValue === false && houseNumber.value) {
			if (userIsLogged.value || registeredUser.value) {
				redirectToCrm(UserState.UPDATE_PROFILE_PAGE_PREFERENCE_ALERTS);
			} else {
				submitHandler();
			}
		}
	});

	async function submitHandler() {
		if (houseNumber.value) {
			errorMessage.value = '';
			isLoading.value = true;

			try {
				const {token} = await executeRecaptcha('requestNewsletter');
				await GsoaContactsService.requestNewsletter({
					requestBody: {
						email: formModel.value.email,
						preferenceId: Number(props.blok.preferenceId),
						globusLocationId: Number(houseNumber.value),
						token: String(token),
					},
				});
				isSent.value = true;
			} catch (e: any) {
				const errorCode = e?.body?.errorCode;

				if (errorCode === 30) {
					registeredUser.value = true;
				} else {
					const messages = e?.body?.messages;
					errorMessage.value = (Array.isArray(messages) ? messages[0] : messages) ?? 'Přihlášení k odběru se nezdařilo.';
				}
			}

			isLoading.value = false;
		} else {
			showHypermarketSelectionBanner.value = true;
		}
	}
</script>
